import { useDispatch, useSelector } from 'react-redux';
import { setCroppedServiceArea } from 'client/store/coverageAssetOwnersSlice/coverageAssetOwnersSlice';
import { buildServiceAreaGeometry, sanitizeTurfGeometry } from 'client/utils/map/serviceAreaUtils';
import { bboxClip as clip } from '@turf/turf';
import { useMapMoveEnd } from './useMapMoveEnd';

const BUFFER = 4000;

export const useScreenBoundingBox = () => {
    const dispatch = useDispatch();
    const myCoverage = useSelector((state) => state?.coverageAssetOwners?.myCoverage);

    const calculateExtent = (map) => {
        if (!myCoverage?.serviceAreas?.length) {
            return;
        }

        const view = map.getView?.();
        const size = map.getSize?.();

        // Avoid OL assertion errors when map/view/size are not ready yet.
        if (!view || !Array.isArray(size) || size.length !== 2 || size[0] <= 0 || size[1] <= 0) {
            return;
        }

        try {
            const serviceAreaGeometry = buildServiceAreaGeometry(myCoverage.serviceAreas);
            if (!serviceAreaGeometry) {
                dispatch(setCroppedServiceArea(null));
                return;
            }

            // Build Turf feature directly from OL coordinates (already EPSG:27700)
            const coords = serviceAreaGeometry.getCoordinates();
            const turfFeature = {
                type: 'Feature',
                geometry: { type: 'MultiPolygon', coordinates: coords },
                properties: {}
            };

            const extent = view.calculateExtent(size);

            const [left, bottom, right, top] = extent;
            const bufferedExtent = [
                left - BUFFER,
                bottom - BUFFER,
                right + BUFFER,
                top + BUFFER
            ];

            const clippedFeature = clip(turfFeature, bufferedExtent);

            // Extract just the geometry from the clipped Feature
            const clippedGeometry = sanitizeTurfGeometry(clippedFeature?.geometry);

            if (!clippedGeometry || !clippedGeometry.coordinates?.length) {
                // Fallback to sanitized full geometry if clipping fails or returns malformed coords.
                dispatch(setCroppedServiceArea(sanitizeTurfGeometry(turfFeature.geometry)));
                return;
            }
            dispatch(setCroppedServiceArea(clippedGeometry));
        } catch (error) {
            // If clipping fails, dispatch null to prevent invalid state
            dispatch(setCroppedServiceArea(null));
        }
    };

    useMapMoveEnd(calculateExtent, [myCoverage]);
};
